import React, { useState } from 'react';

// material UI
import CircularProgress from '@mui/material/CircularProgress';

// theme
import aampeTheme from '../../constants/aampeTheme';

// components
import AampeTooltip, {
    TooltipPlacement,
} from '../shared/AampeTooltip/AampeTooltip';

interface IconButtonProps {
    Icon: any;
    isLoading?: boolean;
    tooltip?: string;
    action: (e?: React.MouseEvent) => void;
    color?: string;
    disabled?: boolean;
    style?: React.CSSProperties;
    size?: number;
    tooltipPlacement?: TooltipPlacement;
}

const IconButton: React.FC<React.PropsWithChildren<IconButtonProps>> = ({
    Icon,
    isLoading,
    tooltip = '',
    action,
    color = aampeTheme.colors.solidA01,
    disabled = false,
    style,
    size,
    tooltipPlacement,
}) => {
    const [hover, setHover] = useState<boolean>(false);
    const handleClick = !disabled ? action : undefined;

    return (
        <>
            {isLoading ? (
                <CircularProgress size={24} />
            ) : (
                <AampeTooltip title={tooltip} placement={tooltipPlacement}>
                    {/*
                        MUI's Tooltip (via AampeTooltip) clones its child and
                        assigns the accessible name (aria-label, from
                        `tooltip`) to *this* div, not to the Icon nested
                        inside it. The click/hover handlers must live here
                        too - not on the Icon - so that the element exposed
                        to assistive tech and to a synthetic/test click
                        (e.g. RTL's `getByLabelText`) is the same element
                        that actually responds to interaction.
                    */}
                    <div
                        // inline-flex shrink-wraps the div to the Icon's
                        // own box (previously the sole hover/click target)
                        // instead of stretching to fill the surrounding
                        // block-level layout, which would otherwise turn
                        // empty space beside the icon into a click/hover
                        // target too.
                        style={{ display: 'inline-flex' }}
                        onClick={handleClick}
                        onMouseEnter={() => {
                            setHover(true);
                        }}
                        onMouseLeave={() => {
                            setHover(false);
                        }}
                    >
                        <Icon
                            size={size}
                            style={{
                                ...style,
                                color: disabled
                                    ? 'lightgray'
                                    : hover
                                    ? aampeTheme.colors.solidA01
                                    : color,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                            }}
                        />
                    </div>
                </AampeTooltip>
            )}
        </>
    );
};

export default React.memo(IconButton);
